import debounce from 'lodash/debounce';
import type { ActionReturn } from 'svelte/action';

export function longpress(
    node: HTMLElement,
    duration: number,
): ActionReturn<number, { 'on:longpress': (e: CustomEvent<void>) => void }> {
    let timer: ReturnType<typeof setTimeout>;

    const handleMousedown = () => {
        timer = setTimeout(() => {
            node.dispatchEvent(new CustomEvent('longpress'));
        }, duration);
    };

    const handleMouseup = () => {
        clearTimeout(timer);
    };

    node.addEventListener('mousedown', handleMousedown);
    node.addEventListener('mouseup', handleMouseup);

    return {
        update(newDuration: number) {
            duration = newDuration;
        },
        destroy() {
            node.removeEventListener('mousedown', handleMousedown);
            node.removeEventListener('mouseup', handleMouseup);
        },
    };
}

export function hover(
    node: HTMLElement,
    { delayIn, delayOut }: { delayIn: number; delayOut: number },
): ActionReturn<
    { delayIn: number; delayOut: number },
    { 'on:hover': (e: CustomEvent<boolean>) => void }
> {
    let timer: ReturnType<typeof setTimeout>;
    const mouseenter = (_e: MouseEvent) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            node.dispatchEvent(new CustomEvent<boolean>('hover', { detail: true }));
        }, delayIn);
    };
    const mouseleave = (_e: MouseEvent) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            node.dispatchEvent(new CustomEvent<boolean>('hover', { detail: false }));
        }, delayOut);
    };

    node.addEventListener('mouseenter', mouseenter);
    node.addEventListener('mouseleave', mouseleave);

    return {
        destroy() {
            node.removeEventListener('mouseenter', mouseenter);
            node.removeEventListener('mouseleave', mouseleave);
        },
    };
}

export function clickOutside(
    node: HTMLElement,
): ActionReturn<undefined, { 'on:outclick': (e: CustomEvent<void>) => void }> {
    const handleClick = (event: MouseEvent) => {
        if (!node.contains(event.target as Node)) {
            node.dispatchEvent(new CustomEvent('outclick'));
        }
    };

    document.addEventListener('click', handleClick, true);

    return {
        destroy() {
            document.removeEventListener('click', handleClick, true);
        },
    };
}

type FetchMoreParams = { scrollFrom: 'window' | 'node' };

export function fetchMore(
    node: HTMLElement,
    options: { scrollFrom: 'window' | 'node' } = { scrollFrom: 'window' },
): ActionReturn<FetchMoreParams, { 'on:fetchmore': (e: CustomEvent<void>) => void }> {
    let scrollPosition = 0;
    let scrollNode = options.scrollFrom === 'window' ? window : node;

    const emit = debounce(() => {
        node.dispatchEvent(new CustomEvent('fetchmore'));
    }, 150);

    let listener = (_event: Event) => {
        let newScrollPosition = scrollNode == node ? node.scrollTop : window.pageYOffset;
        if (newScrollPosition > scrollPosition) {
            let rect = node.getBoundingClientRect();
            if (scrollNode != node) {
                let percentage = (rect.bottom / window.innerHeight) * 100;
                if (rect.bottom <= window.innerHeight && percentage >= 90) {
                    emit();
                }
            } else {
                let percentage =
                    (node.scrollTop / (node.scrollHeight - rect.height)) * 100;
                if (percentage >= 90) {
                    emit();
                }
            }
        }
        scrollPosition = newScrollPosition;
    };

    let wheelListener = debounce(
        (_event: Event) => {
            if (scrollNode != node) {
                if (
                    window.innerHeight + window.scrollY >=
                    document.body.offsetHeight - 10
                ) {
                    emit();
                }
            }
        },
        200,
        { trailing: true },
    );

    scrollNode.addEventListener('scroll', listener);
    scrollNode.addEventListener('wheel', wheelListener);

    return {
        update(opt) {
            scrollNode = opt.scrollFrom === 'window' ? window : node;
            scrollPosition = 0;
        },
        destroy() {
            scrollNode.removeEventListener('scroll', listener);
            scrollNode.removeEventListener('wheel', wheelListener);
        },
    };
}

export function gridArrowNavigation(
    node: HTMLElement,
    selector: string | null = null,
): ActionReturn<string | null, Record<string, unknown>> {
    let listener = (event: KeyboardEvent) => {
        if (!event.key.match(/ArrowUp|ArrowDown|ArrowLeft|ArrowRight/)) return;
        const focusedButton = document.activeElement;
        if (focusedButton == null) return;

        let gridChildren = Array.from(node.children);

        const firstChildTop = gridChildren[0]?.getBoundingClientRect().top ?? 0;

        let numColumns = 0;
        for (const child of gridChildren) {
            const childTop = child.getBoundingClientRect().top;
            if (childTop !== firstChildTop) {
                break; // Reached the end of the first row
            }
            numColumns++;
        }

        if (selector != null) {
            let list: typeof gridChildren = [];
            for (let child of gridChildren) {
                let s = child.querySelector(selector);
                if (s != null) list.push(s);
            }
            gridChildren = list;
        }

        let newIndex;
        let focusedIndex = gridChildren.indexOf(focusedButton);

        if (focusedIndex === -1) return;

        switch (event.key) {
            case 'ArrowUp':
                newIndex = focusedIndex - numColumns;
                break;
            case 'ArrowDown':
                newIndex = focusedIndex + numColumns;
                break;
            case 'ArrowLeft':
                newIndex = focusedIndex - 1;
                break;
            case 'ArrowRight':
                newIndex = focusedIndex + 1;
                break;
            default:
                return;
        }

        if (newIndex >= 0 && newIndex < gridChildren.length) {
            let focusable = gridChildren[newIndex];
            if (focusable != null) {
                (focusable as HTMLElement).focus();
            }
        }
    };
    node.addEventListener('keyup', listener);
    return {
        update(selector_: string | null) {
            selector = selector_;
        },
        destroy() {
            node.removeEventListener('keyup', listener);
        },
    };
}

export function longtouch(
    node: HTMLElement,
    duration: number,
): ActionReturn<number, { 'on:longtouch': (e: CustomEvent<void>) => void }> {
    let timer: ReturnType<typeof setTimeout>;

    const handleTouchDown = () => {
        timer = setTimeout(() => {
            node.dispatchEvent(new CustomEvent('longtouch'));
        }, duration);
    };

    const handleTouchUp = () => {
        clearTimeout(timer);
    };

    node.addEventListener('touchstart', handleTouchDown);
    node.addEventListener('touchend', handleTouchUp);
    node.addEventListener('touchcancel', handleTouchUp);

    return {
        update(newDuration: number) {
            duration = newDuration;
        },
        destroy() {
            node.removeEventListener('touchstart', handleTouchDown);
            node.removeEventListener('touchend', handleTouchUp);
            node.removeEventListener('touchcancel', handleTouchUp);
        },
    };
}

export function isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints;
}
